import type {
	ApiTaskSSEUpdate,
	StreamTaskStatus,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	ExecutionSegment,
	TaskProgressState,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { parseExecutionSegments } from './mergeConversationWithNetworkPriority';
import type { ChatStoreAction } from './types';

type HandleStreamTaskStatusParams = {
	conversationId: string;
	taskMessage: StreamTaskStatus['message'];
	messageId?: string;
	createTaskFromStream: (args: {
		conversationId: string;
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
	}) => ChatStoreAction;
	setTaskProgressState: (args: {
		conversationId: string;
		progressState: TaskProgressState;
		title?: string;
		updatedAt?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		taskId?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
	}) => ChatStoreAction;
	dispatch: (action: ChatStoreAction) => void;
	/** Mirrors the update into the tasks store so rovo-navigation shows the latest status without waiting for the tasks SSE endpoint. */
	applyTaskUpdate?: (event: ApiTaskSSEUpdate) => void;
};

/**
 * Shared handler for TASK_STATUS stream messages.
 *
 * Maps the API task status to the appropriate store action and dispatches it.
 * Used by both `sendMessage` (via `processStream`) and `sendMessageConfirmedTools`
 * to avoid duplicating the status → action mapping logic.
 */
export const handleStreamTaskStatus = ({
	conversationId,
	taskMessage,
	messageId,
	createTaskFromStream,
	setTaskProgressState,
	dispatch,
	applyTaskUpdate,
}: HandleStreamTaskStatusParams): void => {
	applyTaskUpdate?.({
		task_id: taskMessage.task_id,
		title: taskMessage.title,
		status: taskMessage.status,
		updated_at: taskMessage.updated_at,
		current_step: null,
		result_summary: null,
		error_message: null,
	});

	const executionSegments = parseExecutionSegments(taskMessage.execution_segments);

	switch (taskMessage.status) {
		case 'DRAFT':
			dispatch(
				createTaskFromStream({
					conversationId,
					taskId: taskMessage.task_id,
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		case 'COMPLETED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Completed',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					seekingCompletedAck: true,
					completionMessageId: taskMessage.completion_message_id ?? messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		case 'FAILED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Failed',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		case 'CANCELLED':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Cancelled',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		case 'RUNNING':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'Running',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		case 'AWAITING_ACTION_CONFIRMATION':
			dispatch(
				setTaskProgressState({
					conversationId,
					progressState: 'AwaitingActionConfirmation',
					title: taskMessage.title,
					updatedAt: taskMessage.updated_at,
					messageId,
					currentMessageId: taskMessage.current_message_id,
					createdAt: taskMessage.created_at,
					startedAt: taskMessage.started_at,
					completedAt: taskMessage.completed_at,
					executionSegments,
				}),
			);
			break;
		default:
			break;
	}
};
